import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  ChevronLeft, KeyRound, Languages, Trash2, LogOut, AlertTriangle, Loader2,
  Eye, EyeOff, Globe, Check,
} from "lucide-react";

/**
 * V2 / Mobile-native account settings.
 * Provides the actions a player needs without exposing the desktop-styled V1
 * page. Endpoints reused unchanged:
 *   POST   /auth/change-password
 *   POST   /leagues/me/exit
 *   DELETE /users/me
 */
export default function AccountV2() {
  const { user, logout, refreshMe } = useAuth();
  const { t } = useT();
  const nav = useNavigate();

  if (!user) return null;

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-account-page">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
        data-testid="v2-account-back"
      >
        <ChevronLeft className="w-4 h-4" /> {t("v2.match.back")}
      </button>

      <header>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.acc.kicker")}</div>
        <h1 className="text-2xl font-bold text-white mt-1">{t("v2.acc.title")}</h1>
        <p className="text-xs text-[#71717A] mt-1">{user.username} · {user.game_id || "—"}</p>
      </header>

      <LanguageCard t={t} />
      <PasswordCard t={t} />
      {user.league_id && <ExitLeagueCard t={t} refreshMe={refreshMe} />}
      <DangerCard t={t} logout={logout} nav={nav} />
    </div>
  );
}

function Card({ title, kicker, accent = "#7C3AED", icon: Icon, children, testId }) {
  return (
    <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
        <div>
          {kicker && <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: accent }}>{kicker}</div>}
          <h3 className="text-sm font-bold text-white">{title}</h3>
        </div>
      </div>
      {children}
    </section>
  );
}

function LanguageCard({ t }) {
  const { lang, setLang } = useT();
  return (
    <Card title={t("v2.acc.lang.title")} kicker={t("v2.acc.lang.kicker")} icon={Languages} testId="v2-account-language">
      <div className="grid grid-cols-2 gap-2">
        {[
          { code: "en", label: "English" },
          { code: "ar", label: "العربية" },
        ].map((l) => {
          const active = lang === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              data-testid={`v2-account-lang-${l.code}`}
              className={`rounded-xl py-2.5 px-3 inline-flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                active
                  ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/30"
                  : "bg-[#0F0F18] border border-[#1F1F2A] text-white hover:border-[#7C3AED]"
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {l.label}
              {active && <Check className="w-3.5 h-3.5 ml-1" />}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function PasswordCard({ t }) {
  const [oldP, setOldP] = useState("");
  const [newP, setNewP] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newP.length < 6) {
      toast.error(t("v2.acc.pass.errShort"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldP, new_password: newP });
      toast.success(t("v2.acc.pass.toast"));
      setOldP(""); setNewP("");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("v2.acc.pass.title")} kicker={t("v2.acc.pass.kicker")} icon={KeyRound} testId="v2-account-password">
      <form onSubmit={submit} className="space-y-2.5">
        <PasswordField
          label={t("v2.acc.pass.old")}
          value={oldP}
          onChange={setOldP}
          show={showOld}
          toggle={() => setShowOld((s) => !s)}
          testId="v2-account-pass-old"
        />
        <PasswordField
          label={t("v2.acc.pass.new")}
          value={newP}
          onChange={setNewP}
          show={showNew}
          toggle={() => setShowNew((s) => !s)}
          testId="v2-account-pass-new"
        />
        <button
          type="submit"
          disabled={busy || !oldP || !newP}
          data-testid="v2-account-pass-submit"
          className="w-full rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-3 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? t("v2.acc.pass.busy") : t("v2.acc.pass.cta")}
        </button>
      </form>
    </Card>
  );
}

function PasswordField({ label, value, onChange, show, toggle, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
      <div className="flex items-center gap-2 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus-within:border-[#7C3AED] px-3 py-2.5 transition-colors">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={6}
          required
          data-testid={testId}
          className="flex-1 bg-transparent text-white text-sm placeholder-[#52525B] focus:outline-none"
          placeholder="••••••••"
        />
        <button type="button" onClick={toggle} tabIndex={-1} className="text-[#71717A] hover:text-white">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function ExitLeagueCard({ t, refreshMe }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const exit = async () => {
    if (confirm.trim().toUpperCase() !== "EXIT") {
      toast.error(t("v2.acc.exit.errConfirm"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leagues/me/exit", {});
      const n = data.forfeited_matches ?? 0;
      toast.success(data.new_league_id
        ? t("exit.toastJoined", { n })
        : t("exit.toastNoNewLeague", { n }));
      setConfirm("");
      if (refreshMe) await refreshMe();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("v2.acc.exit.title")} kicker={t("v2.acc.exit.kicker")} icon={LogOut} accent="#EA580C" testId="v2-account-exit">
      <p className="text-xs text-[#A1A1AA] leading-relaxed">{t("v2.acc.exit.body")}</p>
      <input
        type="text"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={t("v2.acc.exit.confirmPh")}
        data-testid="v2-account-exit-confirm"
        className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#EA580C] focus:outline-none px-3 py-2.5 text-sm text-white placeholder-[#52525B] transition-colors"
      />
      <button
        type="button"
        onClick={exit}
        disabled={busy || confirm.trim().toUpperCase() !== "EXIT"}
        data-testid="v2-account-exit-submit"
        className="w-full rounded-2xl border border-[#EA580C] text-[#EA580C] font-bold text-sm py-2.5 hover:bg-[#EA580C] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        <LogOut className="w-4 h-4" /> {t("v2.acc.exit.cta")}
      </button>
    </Card>
  );
}

function DangerCard({ t, logout, nav }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    if (confirm.trim().toUpperCase() !== "DELETE") {
      toast.error(t("v2.acc.del.errConfirm"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me", {
        data: { password: pw, confirm: confirm.trim().toUpperCase() },
      });
      toast.success(t("acc.deletedToast", {
        n: data.removed_from_leagues,
        slots: data.slots_reopened ? t("acc.slotsReopened", { n: data.slots_reopened }) : "",
        forfeits: data.forfeited_matches ? t("acc.forfeitedMatches", { n: data.forfeited_matches }) : "",
      }));
      if (logout) await logout();
      nav("/v2", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("v2.acc.del.title")} kicker={t("v2.acc.del.kicker")} icon={Trash2} accent="#E11D48" testId="v2-account-delete">
      <p className="text-xs text-[#A1A1AA] leading-relaxed">{t("v2.acc.del.body")}</p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="v2-account-delete-open"
          className="w-full rounded-2xl border border-[#E11D48] text-[#E11D48] font-bold text-sm py-2.5 hover:bg-[#E11D48] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" /> {t("v2.acc.del.cta")}
        </button>
      ) : (
        <div className="space-y-2.5 rounded-xl bg-[#E11D48]/10 border border-[#E11D48]/30 p-3" data-testid="v2-account-delete-form">
          <div className="flex items-start gap-2 text-[10px] font-mono uppercase tracking-widest text-[#FCA5A5]">
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>{t("v2.acc.del.warn")}</span>
          </div>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder={t("v2.acc.del.passPh")}
            data-testid="v2-account-delete-pass"
            className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#E11D48] focus:outline-none px-3 py-2.5 text-sm text-white placeholder-[#52525B] transition-colors"
          />
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("v2.acc.del.confirmPh")}
            data-testid="v2-account-delete-confirm"
            className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#E11D48] focus:outline-none px-3 py-2.5 text-sm text-white placeholder-[#52525B] transition-colors"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setPw(""); setConfirm(""); }}
              className="rounded-2xl border border-[#1F1F2A] text-[#A1A1AA] font-bold text-xs py-2.5 hover:border-white hover:text-white transition-colors"
              data-testid="v2-account-delete-cancel"
            >
              {t("v2.acc.del.cancel")}
            </button>
            <button
              type="button"
              onClick={doDelete}
              disabled={busy || !pw || confirm.trim().toUpperCase() !== "DELETE"}
              data-testid="v2-account-delete-confirm-btn"
              className="rounded-2xl bg-[#E11D48] text-white font-bold text-xs py-2.5 hover:bg-[#BE123C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              {t("v2.acc.del.confirmCta")}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
